import {
  parseJsonFieldValue,
  resolveJsonPointer,
} from "./build-json-field-tree";

/** One selected series: the pointer to read + the name to show. */
export interface ShotDataField {
  /** JSON Pointer, resolved inside every event. */
  address: string;
  label: string;
  color?: string;
  /** Multiplier applied to the numeric value read at `address` (default 1).
   *  Lets an integer-encoded OPC node (e.g. 408) render as its engineering
   *  value (4.08). Strings pass through unscaled. */
  scale?: number;
}

/** One line to draw. `values[i]` belongs to `events[i]`. */
export interface ShotDataPointSeries {
  name: string;
  values: (number | null)[];
  color?: string;
}

export interface ExtractedShotData {
  /** One entry per event, in event order. */
  times: string[];
  series: ShotDataPointSeries[];
}

/** Read the chosen fields out of the events the endpoint returned.
 *
 *  The four rules the chart depends on (the BE would have to implement the
 *  same ones if extraction ever moves server-side):
 *  1. the pointer is resolved inside EVERY event, not once over the array;
 *  2. ISO-8601 durations become seconds (`parseJsonFieldValue`);
 *  3. a pointer that misses in one event yields `null` there — a gap in that
 *     line, never a failed chart;
 *  4. `occurred_at` is read per event for the x-axis.
 */
export const extractShotDataSeries = (
  events: unknown[],
  fields: ShotDataField[],
): ExtractedShotData => ({
  times: events.map((event) => resolveEventTime(event)),
  series: fields.map((field) => ({
    name: field.label,
    values: events.map((event) => {
      const parsed = parseJsonFieldValue(resolveJsonPointer(event, field.address));
      // Scale numeric values only; a text field (or a miss) stays as-is.
      return typeof parsed === "number" ? parsed * (field.scale ?? 1) : parsed;
    }),
    color: field.color,
  })),
});

/** `occurred_at` as a string, else "" — an unreadable timestamp must not
 *  crash the axis or invent a date. */
const resolveEventTime = (event: unknown): string => {
  const value = resolveJsonPointer(event, "/occurred_at");
  return typeof value === "string" ? value : "";
};
